import { type Article, type Book, type Media } from "@prisma/client";
import {
  json,
  type HeadersFunction,
  type LinksFunction,
  type LoaderFunctionArgs,
  type MetaFunction,
} from "@remix-run/node";
import {
  Form,
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  type ShouldRevalidateFunction,
  useLoaderData,
  useSubmit,
} from "@remix-run/react";
import { withSentry } from "@sentry/remix";
import { useRef } from "react";
import { HoneypotProvider } from "remix-utils/honeypot/react";
import MediaDisplayCard from "#app/components/domain/media-display-card";
import { GeneralErrorBoundary } from "#app/components/error-boundary.tsx";
import { EpicProgress } from "#app/components/progress-bar.tsx";
import { useToast } from "#app/components/toaster.tsx";
import { Button } from "#app/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from "#app/components/ui/dropdown-menu.tsx";
import { Icon, href as iconsHref } from "#app/components/ui/icon.tsx";
import { EpicToaster } from "#app/components/ui/sonner.tsx";
import { useTheme } from "#app/routes/resources+/theme-switch.tsx";
import tailwindStyleSheetUrl from "#app/styles/tailwind.css?url";
import { getUserId, logout } from "#app/utils/auth.server.ts";
import { ClientHintCheck, getHints } from "#app/utils/client-hints.tsx";
import { prisma } from "#app/utils/db.server.ts";
import { MediaType } from "#app/utils/domain/types";
import { getEnv } from "#app/utils/env.server.ts";
import { honeypot } from "#app/utils/honeypot.server.ts";
import useMatchesChildRoute from "#app/utils/hooks/useMatchesChildRoute.tsx";
import {
  cn,
  combineHeaders,
  getDomainUrl,
  getUserImgSrc,
} from "#app/utils/misc.tsx";
import { useNonce } from "#app/utils/nonce-provider.ts";
import { getTheme, type Theme } from "#app/utils/theme.server.ts";
import { makeTimings, time } from "#app/utils/timing.server.ts";
import { getToast } from "#app/utils/toast.server.ts";
import { useOptionalUser, useUser } from "#app/utils/user.ts";
import { type SectionData } from "../prisma/utils/media";
import LLMSidebar from "./components/domain/llm-sidebar";
import sonnerStylesUrl from "./styles/sonner.css?url";
import { ScrollPositionContext } from "./utils/hooks/useScrollPositionContext";
import { getSidebarState } from "./utils/llm-chat/sidebar-state";

export const links: LinksFunction = () => {
  return [
    // Preload svg sprite as a resource to avoid render blocking
    { rel: "preload", href: iconsHref, as: "image" },
    { rel: "stylesheet", href: sonnerStylesUrl },
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/assets/favicons/icons8-bookmark/office-m-16.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/assets/favicons/icons8-bookmark/office-m-32.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "48x48",
      href: "/assets/favicons/icons8-bookmark/office-m-48.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "96x96",
      href: "/assets/favicons/icons8-bookmark/office-m-96.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "120x120",
      href: "/assets/favicons/icons8-bookmark/office-m-120.png",
    },
    {
      rel: "manifest",
      href: "/site.webmanifest",
      crossOrigin: "use-credentials",
    } as const, // necessary to make typescript happy
    { rel: "stylesheet", href: tailwindStyleSheetUrl },
  ].filter(Boolean);
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  return [
    { title: data ? "bookMarks" : "Error | bookMarks" },
    { name: "description", content: `Your own captain's log` },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const timings = makeTimings("root loader");
  const userId = await time(() => getUserId(request), {
    timings,
    type: "getUserId",
    desc: "getUserId in root",
  });

  const user = userId
    ? await time(
        () =>
          prisma.user.findUniqueOrThrow({
            select: {
              id: true,
              name: true,
              username: true,
              image: { select: { id: true } },
              roles: {
                select: {
                  name: true,
                  permissions: {
                    select: { entity: true, action: true, access: true },
                  },
                },
              },
            },
            where: { id: userId },
          }),
        { timings, type: "find user", desc: "find user in root" }
      )
    : null;
  if (userId && !user) {
    console.info("something weird happened");
    // something weird happened... The user is authenticated but we can't find
    // them in the database. Maybe they were deleted? Let's log them out.
    await logout({ request, redirectTo: "/" });
  }
  const { toast, headers: toastHeaders } = await getToast(request);
  const honeyProps = honeypot.getInputProps();

  return json(
    {
      user,
      requestInfo: {
        hints: getHints(request),
        origin: getDomainUrl(request),
        path: new URL(request.url).pathname,
        userPrefs: {
          theme: getTheme(request),
        },
      },
      llmSidebarState: getSidebarState(request),
      ENV: getEnv(),
      toast,
      honeyProps,
    },
    {
      headers: combineHeaders(
        { "Server-Timing": timings.toString() },
        toastHeaders
      ),
    }
  );
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
  const headers = {
    "Server-Timing": loaderHeaders.get("Server-Timing") ?? "",
  };
  return headers;
};

function Document({
  children,
  nonce,
  theme = "light",
  env = {},
  allowIndexing = true,
}: {
  children: React.ReactNode;
  nonce: string;
  theme?: Theme;
  env?: Record<string, string>;
  allowIndexing?: boolean;
}) {
  return (
    <html lang="en" className={`${theme} h-full overflow-x-hidden`}>
      <head>
        <ClientHintCheck nonce={nonce} />
        <Meta />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        {allowIndexing ? null : (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <Links />
      </head>
      <body className="bg-background text-foreground">
        {children}
        <script
          nonce={nonce}
          dangerouslySetInnerHTML={{
            __html: `window.ENV = ${JSON.stringify(env)}`,
          }}
        />
        <ScrollRestoration nonce={nonce} />
        <Scripts nonce={nonce} />
      </body>
    </html>
  );
}

type PartialBookRouteData = {
  book: Book & { media: Media };
  sections: SectionData[];
};

type PartialArticleRouteData = { article: Article & { media: Media } };

function Topbar() {
  const [isSingleBookRouteMatch, bookRouteData] =
    useMatchesChildRoute<PartialBookRouteData>("/_media+/book.$mediaId_");
  const [isSingleArticleRouteMatch, articleRouteData] =
    useMatchesChildRoute<PartialArticleRouteData>("/_media+/article.$mediaId");

  const mediaToDisplay = isSingleBookRouteMatch
    ? bookRouteData.book.media
    : isSingleArticleRouteMatch
      ? articleRouteData.article.media
      : null;
  const hasSections =
    isSingleBookRouteMatch && bookRouteData.sections.length > 0;
  const [isAllBooksRouteMatch, _] = useMatchesChildRoute("/_media+/books_");
  const [isAllArticlesRouteMatch, __] =
    useMatchesChildRoute("/_media+/articles_");

  return (
    <header className="container py-4">
      <div className="flex justify-between">
        <div className="max-w-[60%]">
          {" "}
          {mediaToDisplay && (
            <MediaDisplayCard
              style="header"
              mediaId={mediaToDisplay.id}
              mediaType={mediaToDisplay.type as MediaType}
              title={mediaToDisplay.title}
              author={
                isSingleBookRouteMatch ? bookRouteData.book.author : undefined
              }
              url={
                isSingleArticleRouteMatch
                  ? articleRouteData.article.url
                  : undefined
              }
              thumbnail={
                isSingleBookRouteMatch
                  ? bookRouteData.book.thumbnail
                  : articleRouteData!.article.thumbnail
              }
              enableLink={false}
              showActionMenu={true}
              mediaHasSections={hasSections}
            />
          )}
        </div>
        <nav className="flex flex-wrap items-start justify-end gap-4 sm:flex-nowrap md:gap-8">
          <div className="flex items-center gap-3">
            {isSingleBookRouteMatch ? (
              <AddToMediaDropdown
                mediaId={bookRouteData.book.mediaId}
                mediaType={MediaType.BOOK}
              />
            ) : isSingleArticleRouteMatch ? (
              <AddToMediaDropdown
                mediaId={articleRouteData.article.mediaId}
                mediaType={MediaType.ARTICLE}
              />
            ) : isAllBooksRouteMatch ? (
              <AddBookButton />
            ) : isAllArticlesRouteMatch ? (
              <AddArticleButton />
            ) : (
              <UniversalAddDropdown />
            )}
            <UserDropdown />
          </div>
        </nav>
      </div>
    </header>
  );
}

function useRouteMatches() {
  const isSingleBookRouteMatch = useMatchesChildRoute<PartialBookRouteData>(
    "/_media+/book.$mediaId_"
  )[0];
  const isSingleArticleRouteMatch =
    useMatchesChildRoute<PartialArticleRouteData>(
      "/_media+/article.$mediaId"
    )[0];
  const isInteractiveRouteMatch = useMatchesChildRoute(
    "/excerpts+/from-images.interactive.$mediaId.$uploadId"
  )[0];
  const isUploadRouteMatch = useMatchesChildRoute("/upload")[0];
  const isIndexRouteMatch = useMatchesChildRoute("/index")[0];
  const isAllBooksRouteMatch = useMatchesChildRoute("/_media+/books_")[0];
  const isAllArticlesRouteMatch = useMatchesChildRoute("/_media+/articles_")[0];

  return {
    isSingleBookRouteMatch,
    isSingleArticleRouteMatch,
    isAllBooksRouteMatch,
    isAllArticlesRouteMatch,
    isInteractiveRouteMatch,
    isUploadRouteMatch,
    isIndexRouteMatch,
  };
}

function Sidebar() {
  const {
    isIndexRouteMatch,
    isSingleBookRouteMatch,
    isAllBooksRouteMatch,
    isSingleArticleRouteMatch,
    isAllArticlesRouteMatch,
  } = useRouteMatches();
  const selectedStyling =
    "bg-gray-200 bg-opacity-70 text-gray-800 rounded-md drop-shadow-sm";
  const linkStyling = "px-3 transition duration-300 hover:text-gray-300";
  return (
    <div className="flex h-auto min-h-screen w-52 min-w-44 flex-col justify-start border-r border-slate-300 bg-gray-100 px-1.5 py-5 text-slate-700 dark:bg-gray-400">
      <nav>
        <ul>
          <li className="mb-1">
            <div
              className={cn(
                "w-full py-2",
                isIndexRouteMatch && selectedStyling
              )}
            >
              <Link className={linkStyling} prefetch="intent" to={`/`}>
                Marks
              </Link>
            </div>
          </li>
          <li className="my-3 border-t border-slate-300" />
          <li className="mb-1">
            <div
              className={cn(
                "w-full py-2",
                (isAllBooksRouteMatch || isSingleBookRouteMatch) &&
                  selectedStyling
              )}
            >
              <Link className={linkStyling} prefetch="intent" to={`/books`}>
                Books
              </Link>
            </div>
          </li>
          <li className="mb-1">
            <div
              className={cn(
                "w-full py-2",
                (isAllArticlesRouteMatch || isSingleArticleRouteMatch) &&
                  selectedStyling
              )}
            >
              <Link className={linkStyling} prefetch="intent" to={`/articles`}>
                Articles
              </Link>
            </div>
          </li>
        </ul>
      </nav>
    </div>
  );
}

// revalidate the root loader whenever the chat sidebar state changes
export const shouldRevalidate: ShouldRevalidateFunction = ({
  formAction,
  defaultShouldRevalidate,
}) => {
  if (
    formAction?.includes("/resources/llm-chat/openclose") ||
    formAction?.includes("/resources/llm-chat/chat") ||
    formAction?.includes("/excerpts/delete")
  ) {
    console.log("revalidating root loader");
    return true;
  }

  // For other cases, use the default behavior
  return defaultShouldRevalidate;
};

function App() {
  const data = useLoaderData<typeof loader>();
  const nonce = useNonce();
  const theme = useTheme();
  const user = useOptionalUser();
  const allowIndexing = data.ENV.ALLOW_INDEXING !== "false";
  useToast(data.toast);

  const {
    isSingleBookRouteMatch,
    isSingleArticleRouteMatch,
    isInteractiveRouteMatch,
    isUploadRouteMatch,
    isIndexRouteMatch,
  } = useRouteMatches();

  const isExcerptsListingRoute =
    isSingleBookRouteMatch || isSingleArticleRouteMatch || isIndexRouteMatch;
  const shouldHideMainSidebar = isInteractiveRouteMatch || isUploadRouteMatch;
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const llmSidebarState = data.llmSidebarState;
  return (
    <Document
      nonce={nonce}
      theme={theme}
      allowIndexing={allowIndexing}
      env={data.ENV}
    >
      <div className="flex h-screen w-screen">
        {!shouldHideMainSidebar && user && <Sidebar />}
        <div className="flex h-full flex-1 flex-col">
          {user && <Topbar />}
          <div ref={mainContainerRef} className="h-full flex-1 overflow-auto">
            <ScrollPositionContext.Provider
              value={{
                scrollPosition: mainContainerRef.current?.scrollTop ?? 0,
                setScrollPosition: (
                  scrollPosition: number,
                  behavior: ScrollBehavior = "instant"
                ) => {
                  mainContainerRef.current?.scrollTo({
                    top: scrollPosition,
                    behavior,
                  });
                },
              }}
            >
              <Outlet />
            </ScrollPositionContext.Provider>
          </div>
        </div>
        {user && llmSidebarState?.open && isExcerptsListingRoute && (
          <LLMSidebar
            excerptId={llmSidebarState.excerptId}
            conversationId={llmSidebarState.conversationId}
          />
        )}
      </div>
      <EpicToaster closeButton position="top-center" theme={theme} />
      <EpicProgress />
    </Document>
  );
}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>();
  return (
    <HoneypotProvider {...data.honeyProps}>
      <App />
    </HoneypotProvider>
  );
}

export default withSentry(AppWithProviders);

function UniversalAddDropdown() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button size="lg">
          <Icon name="plus" size="lg" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent sideOffset={8} align="end" className="min-w-40">
          <DropdownMenuItem asChild>
            <Link prefetch="intent" to={`/excerpts/add/from-images`}>
              <Icon className="text-body-lg" name="pencil-2">
                Excerpts
              </Icon>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link prefetch="intent" to={`/books/add`}>
              <Icon className="text-body-lg" name="book">
                Book
              </Icon>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link prefetch="intent" to={`/articles/add`}>
              <Icon className="text-body-lg" name="reader">
                Article
              </Icon>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}

function AddToMediaDropdown({
  mediaId,
  mediaType,
}: {
  mediaId: string;
  mediaType: MediaType;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <Button size="lg">
          <Icon name="plus" size="lg" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent sideOffset={8} align="end" className="min-w-40">
          <DropdownMenuItem asChild>
            <Link
              prefetch="intent"
              to={`/excerpts/add/${mediaType === MediaType.BOOK ? "from-images" : "text-entry"}?mediaId=${mediaId}`}
            >
              <Icon name="quote" size="lg">
                Excerpts
              </Icon>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link prefetch="intent" to={`/notes/add/${mediaId}`}>
              <Icon className="text-body-lg" name="pencil-2">
                Note
              </Icon>
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}

function AddBookButton() {
  return (
    <Link prefetch="intent" to={`/books/add`}>
      <Button size="lg">
        <Icon name="plus" size="lg" />
      </Button>
    </Link>
  );
}

function AddArticleButton() {
  return (
    <Link prefetch="intent" to={`/articles/add`}>
      <Button size="lg">
        <Icon name="plus" size="lg" />
      </Button>
    </Link>
  );
}

function UserDropdown() {
  const user = useUser();
  const submit = useSubmit();
  const formRef = useRef<HTMLFormElement>(null);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button asChild variant="secondary" className="bg-gray-400">
          <Link
            to={`/users/${user.username}`}
            // this is for progressive enhancement
            onClick={(e) => e.preventDefault()}
            className="flex items-center gap-2"
          >
            <img
              className="h-6 w-6 rounded-full object-cover"
              alt={user.name ?? user.username}
              src={getUserImgSrc(user.image?.id)}
            />
          </Link>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            asChild
            // this prevents the menu from closing before the form submission is completed
            onSelect={(event) => {
              event.preventDefault();
              submit(formRef.current);
            }}
          >
            <Form action="/logout" method="POST" ref={formRef}>
              <Icon className="text-body-md" name="exit">
                <button type="submit">Logout</button>
              </Icon>
            </Form>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}

export function ErrorBoundary() {
  // the nonce doesn't rely on the loader so we can access that
  const nonce = useNonce();

  // NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
  // likely failed to run so we have to do the best we can.
  // We could probably do better than this (it's possible the loader did run).
  // This would require a change in Remix.

  // Just make sure your root route never errors out and you'll always be able
  // to give the user a better UX.

  return (
    <Document nonce={nonce}>
      <GeneralErrorBoundary />
    </Document>
  );
}
